import React, { useEffect, useState } from "react";
import { createTransaction } from "../../../../api/transaction";
import {
  createIntradayTrade,
  closeIntradayTrade,
} from "../../../../api/intradatTrade";
import { getWalletTransactionsSummary } from "../../../../api/walletTransaction";
import { usePrices } from "../../../../context/SocketContext";
import { useAuth } from "../../../../context/AuthContext";
import Swal from "sweetalert2";

const OrderForm = ({
  type, // "buy" | "sell"
  coin,
  from,
  onTradeSuccess,
  orderDetails,
}) => {
  const { user } = useAuth();
  const prices = usePrices();

  const [tradeMode, setTradeMode] = useState("Spot"); // "Spot" | "Futures"
  const [orderType, setOrderType] = useState("market"); // "market" | "limit"

  const [quantity, setQuantity] = useState(1);
  const [price, setPrice] = useState(0);
  const [stopLoss, setStopLoss] = useState("");
  const [takeProfit, setTakeProfit] = useState("");
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(0);
  const [total, setTotal] = useState("0.0000");
  const [isValid, setIsValid] = useState(false);
  const [maxQuantity, setMaxQuantity] = useState(0);
  const [leverage, setLeverage] = useState(false);

  // sync maxQuantity
  useEffect(() => {
    setMaxQuantity(orderDetails?.quantity || 0);
  }, [orderDetails]);

  // fetch balance
  const [refreshQuantity, setRefreshQuantity] = useState(false);
  useEffect(() => {
    if (!user?.id) return;
    getWalletTransactionsSummary()
      .then((summary) => {
        setBalance(summary?.totalBalance ?? 0);
      })
      .catch(() => setBalance(0));
  }, [user, refreshQuantity]);

  const handleTradeSuccess = () => {
    onTradeSuccess?.();
    setRefreshQuantity((prev) => !prev);
  };

  // live price sync
  useEffect(() => {
    if (!coin || !prices.length) return;
    const coinData = prices.find((item) => item.coinId === coin.id);
    if (!coinData) return;

    const livePrice = Number(coinData.price);
    if (orderType === "market") {
      setPrice(livePrice);
    } else {
      setPrice((prev) => (prev <= 0 ? livePrice : prev));
    }
  }, [coin, prices, orderType, type]);

  // total calc
  useEffect(() => {
    const q = parseFloat(quantity) || 0;
    const p = parseFloat(price) || 0;
    setTotal((q * p).toFixed(4));
  }, [price, quantity]);

  // validation
  useEffect(() => {
    const q = parseFloat(quantity);
    const p = parseFloat(price);
    const sl = parseFloat(stopLoss);
    const tp = parseFloat(takeProfit);

    let valid = true;
    if (isNaN(q) || q <= 0) valid = false;
    if (isNaN(p) || p <= 0) valid = false;
    if (type === "buy" && balance !== null && p * q > balance) {
      valid = false;
    }
    if (type === "sell" && tradeMode === "Spot" && q > maxQuantity) {
      valid = false;
    }

    if (
      tradeMode === "Futures (100x)" &&
      ((stopLoss && (isNaN(sl) || sl <= 0)) ||
        (takeProfit && (isNaN(tp) || tp <= 0)))
    ) {
      valid = false;
    }
    if (balance == 0 || !balance || balance == null) {
      valid = false;
    }
    setIsValid(valid);
  }, [
    type,
    tradeMode,
    price,
    quantity,
    stopLoss,
    takeProfit,
    balance,
    maxQuantity,
  ]);

  // trade handler
  // trade handler
  const handleTrade = async () => {
    if (!isValid) {
      Swal.fire({
        icon: "error",
        title: "Invalid Trade",
        text: "Please check your inputs and try again.",
      });
      return;
    }

    setLoading(true);
    try {
      let res;
      if (tradeMode === "Futures (100x)") {
        res = await createIntradayTrade({
          userId: user?.id || "",
          coinId: coin.id,
          direction: type === "buy" ? "long" : "short",
          entryPrice: parseFloat(price),
          quantity: parseFloat(quantity),
          orderType,
          leverage: leverage ? 100 : 1,
          stopLoss: stopLoss ? parseFloat(stopLoss) : null,
          takeProfit: takeProfit ? parseFloat(takeProfit) : null,
        });
      } else {
        const payload = {
          tokenId: coin.id,
          quantity: parseFloat(quantity),
          transactionType: type,
          orderType,
          ...(orderType === "limit" && { price: parseFloat(price) }), // price only for limit
        };
        res = await createTransaction(payload);
      }
      if (res === 200 || res === 201) {
        setQuantity(1);
        setStopLoss("");
        setTakeProfit("");
        handleTradeSuccess();
        Swal.fire({
          icon: "success",
          title: "Trade Successful",
          text: `Your ${type} order was placed successfully.`,
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({ icon: "error", title: "Transaction Failed" });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An error occurred while processing the transaction",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseTrade = async () => {
    if (!orderDetails) return;
    setLoading(true);
    try {
      const res = await closeIntradayTrade(orderDetails?.id);
      console.log("RUNNER ORDER DETAILS111", orderDetails);

      if (res === 200 || res === 201) {
        handleTradeSuccess();
        Swal.fire({
          icon: "success",
          title: "Trade Closed",
          text: "Your futures position has been closed successfully.",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({ icon: "error", title: "Close Trade Failed" });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An error occurred while closing the trade",
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (orderDetails && from == "Futures") {
      setTradeMode("Futures (100x)");
      setLeverage(orderDetails?.leverage);
      setStopLoss(orderDetails?.stopLoss);
      setTakeProfit(orderDetails?.takeProfit);
    }
  }, [orderDetails, from]);

  return (
    <form className="form-wrapper trade-form">
      {/* Trade Mode & Order Type */}
      <div className="d-flex gap-2 mb-3">
        {/* Hide orderType dropdown if from is not null */}
        {from == null && (
          <select
            className="form-select"
            value={orderType}
            onChange={(e) => setOrderType(e.target.value)}
          >
            <option value="market">Market</option>
            <option value="limit">Limit</option>
          </select>
        )}

        <select
          className="form-select"
          value={tradeMode}
          onChange={(e) => setTradeMode(e.target.value)}
          disabled={from != null} // lock the selection if from is passed
        >
          {from == null ? (
            <>
              <option value="Spot">Spot</option>
              <option value="Futures (100x)">Futures (100x)</option>
            </>
          ) : (
            <option value={from}>{from}</option>
          )}
        </select>
      </div>

      {/* Leverage Switch (only for Futures) */}
      {tradeMode === "Futures (100x)" && (
        <div className="input-group form-control mb-3 d-flex justify-content-between p-0">
          <span className="input-group-text">
            Leverage ({leverage ? "1:100" : "1:1"})
          </span>
          <div className="form-check form-switch d-grid">
            <input
              className={`form-check-input ${leverage ? "bg-primary" : ""}`}
              type="checkbox"
              id="leverageSwitch"
              checked={leverage}
              disabled={orderDetails}
              onChange={(e) => setLeverage(e.target.checked)}
              style={{
                width: "56px",
                height: "28px",
                backgroundColor: leverage ? "#4ade80" : "#d1d5db",
                borderColor: "transparent",
                cursor: "pointer",
              }}
            />
          </div>
        </div>
      )}

      {/* Quantity */}
      <div className="input-group mb-3">
        <span className="input-group-text">Quantity</span>
        <input
          className="form-control text-end"
          type="number"
          min={0}
          value={quantity}
          disabled={from === "Futures" && orderDetails}
          onChange={(e) => setQuantity(Math.max(0, Number(e.target.value)))}
        />
      </div>

      {/* Price */}
      <div className="input-group mb-3">
        <span className="input-group-text">Price</span>
        <input
          className="form-control text-end"
          type="number"
          value={price}
          onChange={(e) =>
            orderType === "limit"
              ? setPrice(Math.max(1, Number(e.target.value)))
              : null
          }
          disabled={orderType === "market"}
          style={
            orderType === "market"
              ? { backgroundColor: "#e9ecef", opacity: 1 }
              : {}
          }
        />
      </div>

      {/* Futures only: SL/TP */}
      {tradeMode === "Futures (100x)" && (
        <>
          <div className="input-group mb-3">
            <span className="input-group-text">Stop Loss</span>
            <input
              className="form-control text-end"
              type="number"
              disabled={orderDetails}
              placeholder="e.g. 25500"
              value={stopLoss}
              onChange={(e) => setStopLoss(e.target.value)}
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text">Take Profit</span>
            <input
              className="form-control text-end"
              type="number"
              disabled={orderDetails}
              placeholder="e.g. 27000"
              value={takeProfit}
              onChange={(e) => setTakeProfit(e.target.value)}
            />
          </div>
        </>
      )}

      {/* Total */}
      <div className="input-group mb-3">
        <span className="input-group-text">Total</span>
        <input
          className="form-control text-end"
          type="text"
          value={leverage ? total / 100 : total}
          disabled
          style={{ backgroundColor: "#e9ecef", opacity: 1 }}
        />
      </div>

      {/* Submit */}
      {from === "Futures" && orderDetails ? (
        <button
          type="button"
          className="btn w-100 btn-warning"
          onClick={handleCloseTrade}
          disabled={loading}
        >
          {loading ? "Closing..." : "Close Trade"}
        </button>
      ) : (
        <button
          type="button"
          className={`btn w-100 ${
            type === "buy" ? "btn-success" : "btn-danger"
          }`}
          onClick={handleTrade}
          disabled={loading || !isValid}
        >
          {loading ? "Processing..." : type === "buy" ? "Buy" : "Sell"}
        </button>
      )}
    </form>
  );
};

export default OrderForm;
